/**
 * ShopAPI — API client for shop endpoints (packs, purchases, pity)
 */

import { loadAccessToken } from './NetworkManager';

const SERVER_URL = import.meta.env.VITE_SERVER_URL as string || (
  typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? `${window.location.protocol}//${window.location.host}`
    : 'http://localhost:8080'
);

// --- Types ---

export interface PityCounter {
  player_id: string;
  pack_id: string;
  sr_pity: number;
  ssr_pity: number;
  total_pulls: number;
  updated_at: string;
}

export interface CardDefinition {
  id: string;
  code: string;
  name: string;
  faction: string;
  faction_zh: string;
  type: string;
  type_zh: string;
  atk: number | null;
  score: number | null;
  sacrifice: number | null;
  rarity: string;
  effect: string;
  special_type: string;
  pack: string;
  image_url: string;
  is_active: boolean;
}

export interface CardPackWeight {
  id: string;
  pack_id: string;
  rarity: string;
  weight: number;
}

export interface CardPack {
  id: string;
  name: string;
  description: string;
  price: number;
  cards_per_pack: number;
  is_active: boolean;
  weights: CardPackWeight[];
}

export interface PurchaseResult {
  pack_name: string;
  drawn_cards: CardDefinition[];
  coins_left: number;
  pity?: PityCounter;
}

export interface PlayerWallet {
  player_id: string;
  coins: number;
  dust: number;
  updated_at: string;
}

export interface DisenchantResult {
  card_code: string;
  rarity: string;
  dust_gained: number;
  dust_balance: number;
}

export interface CraftResult {
  card_code: string;
  rarity: string;
  dust_spent: number;
  dust_balance: number;
}

export interface BulkDisenchantResult {
  cards_disenchanted: number;
  total_dust_gained: number;
  dust_balance: number;
  details: Array<{
    card_code: string;
    rarity: string;
    count: number;
    dust: number;
  }>;
}

export interface DustRates {
  [rarity: string]: {
    disenchant: number;
    craft: number;
  };
}

export interface LoginRewardDay {
  day: number;
  coins: number;
  dust?: number;
  free_pack?: boolean;
}

export interface LoginRewardState {
  current_day: number;
  claimable: boolean;
  today_reward: LoginRewardDay;
  last_claim?: string;
}

export interface LoginRewardResult {
  day: number;
  coins_gained: number;
  dust_gained: number;
  free_pack?: PurchaseResult;
  next_day: number;
  wallet_coins: number;
  wallet_dust: number;
}

// --- Helpers ---

class ShopApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ShopApiError';
    this.status = status;
  }
}

async function shopRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = loadAccessToken();
  if (!token) {
    throw new ShopApiError(401, '未登入');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const res = await fetch(`${SERVER_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data: unknown = await res.json();

  if (!res.ok) {
    const errorData = data as { error?: string };
    throw new ShopApiError(res.status, errorData.error ?? '未知錯誤');
  }

  return data as T;
}

// --- Shop APIs ---

/** List all active card packs. */
export async function listPacks(): Promise<CardPack[]> {
  return shopRequest<CardPack[]>('GET', '/api/shop/packs');
}

/** Purchase a card pack. */
export async function purchasePack(packId: string): Promise<PurchaseResult> {
  return shopRequest<PurchaseResult>('POST', `/api/shop/packs/${packId}/purchase`);
}

/** Get current wallet. */
export async function getWallet(): Promise<PlayerWallet> {
  return shopRequest<PlayerWallet>('GET', '/api/shop/wallet');
}

/** Get pity counter for a specific pack. */
export async function getPityCounter(packId: string): Promise<PityCounter> {
  return shopRequest<PityCounter>('GET', `/api/shop/pity?pack_id=${encodeURIComponent(packId)}`);
}

// --- Dust System APIs ---

/** Disenchant a single card. */
export async function disenchant(cardCode: string): Promise<DisenchantResult> {
  return shopRequest<DisenchantResult>('POST', '/api/shop/disenchant', { card_code: cardCode });
}

/** Craft a card from dust. */
export async function craft(cardCode: string): Promise<CraftResult> {
  return shopRequest<CraftResult>('POST', '/api/shop/craft', { card_code: cardCode });
}

/** Disenchant all duplicate cards. */
export async function disenchantDuplicates(): Promise<BulkDisenchantResult> {
  return shopRequest<BulkDisenchantResult>('POST', '/api/shop/disenchant-duplicates');
}

/** Get dust rates for all rarities. */
export async function getDustRates(): Promise<DustRates> {
  return shopRequest<DustRates>('GET', '/api/shop/dust-rates');
}

// --- Login Reward APIs ---

/** Get current login reward state. */
export async function getLoginRewardState(): Promise<LoginRewardState> {
  return shopRequest<LoginRewardState>('GET', '/api/login-reward');
}

/** Claim today's login reward. */
export async function claimLoginReward(): Promise<LoginRewardResult> {
  return shopRequest<LoginRewardResult>('POST', '/api/login-reward/claim');
}
